import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import type { SyntheticEvent} from 'react';

import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import type {
  AutocompleteOwnerState,
  AutocompleteRenderOptionState,
  AutocompleteValue,
  ChipTypeMap} from '@mui/material';
import {
  Autocomplete,
  autocompleteClasses,
  Typography,
  unstable_composeClasses,
} from '@mui/material'
import classnames from 'classnames'
import isEmpty from 'lodash/isEmpty'

import Button from '../ButtonNewest/Button'
import CheckboxField from '../CheckboxField/CheckboxField'
import Chip from '../Chip/Chip'
import FeatherIcon from '../FeatherIcon/FeatherIcon'
import { IconName } from '../FeatherIcon/FeatherIcon.types'
import useAriaProps from '../Field/hooks/useAriaProps'
import useFieldSlotProps from '../Field/hooks/useFieldSlotProps'
import FieldWrapper from '../FieldWrapper/FieldWrapper'
import TextFieldBase from '../TextFieldBase/TextFieldBase'
import type { AutocompleteFieldProps, AutocompleteOption } from './AutocompleteField.types'
import autocompleteFieldClasses, { getAutocompleteFieldUtilityClass } from './autoCompleteFieldClasses'
import AutocompleteFieldListBox from './AutocompleteFieldListBox'
import useAsyncAutocomplete from './useAsyncAutocomplete'
import { styled } from '@mui/material/styles'

const AutocompleteFieldRoot = styled(Autocomplete, {
  name: 'AutocompleteField',
  slot: 'Root',
  overridesResolver: (props, styles) => styles.root,
})(({ theme, disabled, loading }) => ({
  '@keyframes pulse': {
    '0%, 100%': {
      backgroundColor: '#fff',
    },
    '50%': {
      backgroundColor: '#e9e9ea',
    },
  },

  [`& .${autocompleteFieldClasses.field}`]: {
    width: '100%',
  },

  [`& .${autocompleteClasses.inputRoot}`]: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: theme.spacing(1),

    ...(disabled && {
      background: 'rgb(245, 246, 250)',
    }),

    ...(loading && {
      animation: 'pulse 2s infinite',
    }),

    [`& .${autocompleteFieldClasses.select}`]: {
      background: 'transparent',
      flex: 1,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: theme.spacing(1),
    },

    [`& .${autocompleteFieldClasses.searchIcon}`]: {
      '& svg': {
        stroke: 'rgba(0, 0, 0, 0.54)',
      },
    },
  },
})) as typeof Autocomplete

const AutocompleteEndAdornment = styled('div', {
  name: 'AutocompleteField',
  slot: 'EndAdornment',
  overridesResolver: (props, styles) => styles.endAdornment,
})({
  // We use a position absolute to support wrapping tags.
  position: 'absolute',
  right: 0,
  top: '50%',
  transform: 'translate(0, -50%)',
  display: 'flex',
})

const AutocompleteClearSearchButton = styled(Button, {
  name: 'AutocompleteField',
  slot: 'ClearSearchButton',
  overridesResolver: (props, styles) => styles.clearSearchButton,
})({
  marginRight: 12,
})

const useUtilityClasses = <
  Option extends AutocompleteOption,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
  QueryVariables extends Record<string, unknown> = Record<string, unknown>,
  MoreQueryVariables extends Record<string, unknown> = Record<string, unknown>,
  QueryResults extends unknown = Record<string, unknown>,
  MoreQueryResults extends unknown = QueryResults,
>(
  ownerState: Partial<
    AutocompleteFieldProps<
      Option,
      Multiple,
      DisableClearable,
      FreeSolo,
      ChipComponent,
      QueryVariables,
      MoreQueryVariables,
      QueryResults,
      MoreQueryResults
    >
  >,
) => {
  const slots = {
    root: ['root', ownerState.layout || 'row', ownerState.size || 'medium'],
    field: ['field'],
    label: ['label'],
    select: ['select'],
    helperText: ['helperText'],
    errorText: ['errorText'],
    list: ['list'],
    option: ['option'],
    placeholder: ['placeholder'],
    searchIcon: ['searchIcon'],
    endAdornment: ['endAdornment'],
    clearSearchButton: ['clearSearchButton'],
  }

  return unstable_composeClasses(slots, getAutocompleteFieldUtilityClass, ownerState.classes)
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />
const SEARCH_MODE_FILTER_OPTIONS = <Option extends AutocompleteOption>(options: Option[]) => options

function _AutocompleteField<
  Option extends AutocompleteOption,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
  QueryVariables extends Record<string, unknown> = Record<string, unknown>,
  MoreQueryVariables extends Record<string, unknown> = Record<string, unknown>,
  QueryResults extends unknown = Record<string, unknown>,
  MoreQueryResults extends unknown = QueryResults,
>(
  props: AutocompleteFieldProps<
    Option,
    Multiple,
    DisableClearable,
    FreeSolo,
    ChipComponent,
    QueryVariables,
    MoreQueryVariables,
    QueryResults,
    MoreQueryResults
  >,
) {
  const {
    className,
    name,
    label,
    error,
    helperText,
    WrapperProps,
    InputLabelProps,
    FormHelperTextProps,
    ErrorTextProps,
    TextFieldProps,
    ListboxProps,
    placeholder = 'Select an item...',
    layout = 'row',
    size = 'medium',
    value,
    options: optionsProp,
    renderOption: renderOptionProp,
    asyncMode = 'static',
    loadData,
    loadMore,
    getOptions,
    getQueryVariables,
    getPageCursor,
    loadChildData,
    optionHasChild,
    getChildOptions,
    getChildQueryVariables,
    onChange,
    onBlur,
    onInputChange,
    isOptionEqualToValue: isOptionEqualToValueProp,
    searchDebounceTimeout,
    minimumSearchLength,
    checkbox,
    multiControls,
    clearText = 'Clear',
    responsive,
    optionClassName,
    ...rootProps
  } = props

  if (!loadData && !optionsProp) {
    console.error('You must provide either `options` or `loadData` prop')
  }

  if (loadData && optionsProp) {
    console.warn(
      'Providing both `options` & `loadData` props will result in `options` being ignored. Is this what you want?',
    )
  }

  const slotClasses = useUtilityClasses({ ...props, layout, size, disabled: props.disabled })

  const { ariaId, labelId, fieldAriaProps } = useAriaProps(props)

  const { labelProps, helperTextProps, errorProps } = useFieldSlotProps(slotClasses, {
    ...props,
    FormHelperTextProps,
    ErrorTextProps,
    InputLabelProps,
  })

  const isAsync = Boolean(loadData)

  const asyncOptions = useAsyncAutocomplete<Option, QueryVariables, MoreQueryVariables, QueryResults, MoreQueryResults>(
    {
      defaultValue: value,
      skip: !isAsync,
      mode: asyncMode,
      loadData: loadData!,
      loadMore,
      loadChildData,
      optionHasChild,
      getChildOptions,
      getChildQueryVariables,
      getOptions,
      getQueryVariables,
      getPageCursor,
      onChange: onChange as AutocompleteFieldProps<Option, boolean, boolean, boolean>['onChange'],
      onInputChange,
      searchThrottleTimeout: searchDebounceTimeout,
      minimumSearchLength,
      multiple: Boolean(props.multiple),
    },
  )

  const { fieldProps: asyncFieldProps, listboxProps: asyncListboxProps, waiting } = asyncOptions || {}

  const { onClearAll: asyncOnClearAll, ...listboxProps } = asyncListboxProps || {}

  const initialValueHandledRef = useRef(!waiting && !(asyncMode === 'search' && value))

  const options = useMemo(() => {
    if (loadData) {
      return asyncFieldProps?.options
    }

    if (!optionsProp) {
      return []
    }

    return getOptions?.(optionsProp) ?? optionsProp
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asyncFieldProps?.options, getOptions, loadData, optionsProp])

  const optionFromValue = useMemo<AutocompleteValue<Option, boolean, DisableClearable, FreeSolo>>(() => {
    if (asyncMode === 'search') {
      return null as AutocompleteValue<Option, boolean, DisableClearable, FreeSolo>
    }

    if (props.multiple) {
      return Array.isArray(value)
        ? value.reduce(
            (acc, val) => {
              const option = options?.find((option) => option.value === val)

              if (option) {
                acc.push(option)
              }

              return acc
            },
            [] as AutocompleteValue<Option, true, DisableClearable, FreeSolo>,
          )
        : ([] as AutocompleteValue<Option, true, DisableClearable, FreeSolo>)
    }

    return (
      options?.length && (typeof value === 'string' || typeof value === 'number')
        ? options?.find((option) => option.value === value)
        : value
    ) as AutocompleteValue<Option, false, DisableClearable, FreeSolo>
  }, [asyncMode, options, props.multiple, value])

  useEffect(() => {
    if (initialValueHandledRef.current) {
      return
    }

    if (asyncMode === 'search') {
      setFieldValue(value as AutocompleteValue<Option, false, DisableClearable, FreeSolo>)
      initialValueHandledRef.current = true
      return
    }

    if (options?.length && optionFromValue) {
      setFieldValue(optionFromValue)
      initialValueHandledRef.current = true
    }
  }, [asyncMode, optionFromValue, options, options?.length, value, waiting])

  const resetValue = useRef<AutocompleteValue<Option, boolean, DisableClearable, FreeSolo>>(props.multiple ? [] : null)

  const [fieldValue, setFieldValue] = React.useState<AutocompleteValue<
    Option,
    boolean,
    DisableClearable,
    FreeSolo
  > | null>((initialValueHandledRef.current && optionFromValue) || resetValue.current)

  const [inputValue, setInputValue] = React.useState('')

  const handleInputChange = useCallback<
    Required<AutocompleteFieldProps<Option, Multiple, DisableClearable, FreeSolo>>['onInputChange']
  >(
    (event, newInputValue, reason) => {
      setInputValue(newInputValue)
      onInputChange?.(event, newInputValue, reason)
    },
    [onInputChange],
  )

  const getOptionLabel = useCallback<
    Required<AutocompleteFieldProps<Option, Multiple, DisableClearable, FreeSolo>>['getOptionLabel']
  >(
    (value) => {
      if (value && rootProps.getOptionLabel) {
        return rootProps.getOptionLabel(value)
      }

      const options = asyncFieldProps?.options || optionsProp

      const label =
        typeof value === 'number' || typeof value === 'string'
          ? options?.find((option) => option.value === value)?.label
          : value.label

      return label ?? ''
    },
    [asyncFieldProps?.options, optionsProp, rootProps],
  )

  const isOptionEqualToValue = useCallback<
    Required<AutocompleteFieldProps<Option, Multiple, DisableClearable, FreeSolo>>['isOptionEqualToValue']
  >(
    (option, value) => {
      if (isOptionEqualToValueProp) {
        return isOptionEqualToValueProp(option, value)
      }

      if (typeof value === 'object' && 'value' in value) {
        return option.value === value.value
      }

      return option.value === value
    },
    [isOptionEqualToValueProp],
  )

  const renderOption = useMemo(() => {
    const defaultRenderOption: Required<
      AutocompleteFieldProps<Option, Multiple, DisableClearable, FreeSolo, ChipComponent>
    >['renderOption'] = (props2: { key?: string } & React.HTMLAttributes<HTMLLIElement>, option, { selected }) => {
      const { key, ...otherProps } = props2

      return (
        <li key={key} {...otherProps} className={classnames(optionClassName, otherProps.className, slotClasses.option)}>
          {checkbox && (
            <CheckboxField name={`${name}-${option.value}`} icon={icon} checkedIcon={checkedIcon} checked={selected} />
          )}
          {getOptionLabel?.(option) ?? option.label}
        </li>
      )
    }

    return renderOptionProp
      ? (
          props: { key: string } & React.HTMLAttributes<HTMLLIElement>,
          option: Option,
          state: AutocompleteRenderOptionState,
          ownerState: AutocompleteOwnerState<Option, Multiple, DisableClearable, FreeSolo, ChipComponent>,
        ) =>
          renderOptionProp(
            { ...props, className: classnames(optionClassName, slotClasses.option) },
            option,
            state,
            ownerState,
          )
      : defaultRenderOption
  }, [renderOptionProp, optionClassName, slotClasses.option, checkbox, name, getOptionLabel])

  const asyncOnChange = asyncFieldProps?.onChange

  const handleChange = useCallback<
    Required<AutocompleteFieldProps<Option, Multiple, DisableClearable, FreeSolo, ChipComponent>>['onChange']
  >(
    (...args) => {
      setFieldValue(args[1])

      if (asyncOnChange) {
        asyncOnChange(...args)
      } else if (onChange) {
        onChange(...args)
      }
    },
    [asyncOnChange, onChange],
  )

  const handleSelectAll = useCallback(() => {
    if (options?.length) {
      setFieldValue(options)
    }
  }, [options])

  const handleClearAll = useCallback(() => {
    setFieldValue(resetValue.current)

    if (asyncOnClearAll) {
      asyncOnClearAll?.()
    } else {
      setInputValue('')
    }

    onChange?.(
      {} as SyntheticEvent,
      resetValue.current as AutocompleteValue<Option, Multiple, DisableClearable, FreeSolo>,
      'clear',
    )
  }, [asyncOnClearAll, onChange])

  const handleClearInput = useCallback(() => {
    setFieldValue(resetValue.current)

    if (asyncOnClearAll) {
      asyncOnClearAll?.()
    } else {
      setInputValue('')
    }

    if (asyncMode === 'search' && !props.multiple) {
      asyncFieldProps?.onClose()
    }

    onChange?.(
      {} as SyntheticEvent,
      resetValue.current as AutocompleteValue<Option, Multiple, DisableClearable, FreeSolo>,
      'clear',
    )
  }, [asyncFieldProps, asyncMode, asyncOnClearAll, onChange, props.multiple])

  const disabled = props.disabled || (asyncFieldProps?.loading && !asyncFieldProps?.open)

  const renderTags = useCallback<
    Required<AutocompleteFieldProps<Option, Multiple, DisableClearable, FreeSolo, ChipComponent>>['renderTags']
  >(
    (tagValue, getTagProps) => {
      const tagLimit = props.limitTags || 3

      if (tagValue.length > tagLimit) {
        return <Typography>{`${tagValue.length} Selected`}</Typography>
      } else {
        return tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index })

          return (
            <Chip key={key} label={option.label} {...tagProps} size={props.size === 'large' ? 'medium' : 'small'} />
          )
        })
      }
    },
    [props.limitTags, props.size],
  )

  const handleBlur = useCallback<
    Required<AutocompleteFieldProps<Option, Multiple, DisableClearable, FreeSolo, ChipComponent>>['onBlur']
  >(
    (event) => {
      if (fieldValue && !value && props.clearOnBlur) {
        setFieldValue(resetValue.current)
      }

      if (onBlur) {
        onBlur(event)
      }
    },
    [fieldValue, onBlur, props.clearOnBlur, value],
  )

  return (
    <FieldWrapper
      {...WrapperProps}
      name={name}
      label={label}
      layout={layout}
      responsive={responsive}
      error={error}
      helperText={helperText}
      size={size}
      className={classnames(slotClasses.root, WrapperProps?.className)}
      InputLabelProps={{ ...InputLabelProps, ...labelProps, id: labelId }}
      FormHelperTextProps={{ ...FormHelperTextProps, ...helperTextProps }}
      ErrorTextProps={{ ...ErrorTextProps, ...errorProps }}
    >
      <AutocompleteFieldRoot
        {...rootProps}
        // @ts-expect-error - MUI types are incorrect for custom components
        ListboxComponent={AutocompleteFieldListBox}
        ListboxProps={{
          ...ListboxProps,
          className: classnames(ListboxProps?.className, slotClasses.list),
          // @ts-expect-error - MUI types are incorrect for custom components
          multiControls,
          value,
          selectedCount: Array.isArray(fieldValue) ? fieldValue?.length : undefined,
          optionCount: options?.length,
          onClearAll: handleClearAll,
          onSelectAll: handleSelectAll,
          ...listboxProps,
        }}
        value={fieldValue as AutocompleteValue<Option, Multiple, DisableClearable, FreeSolo>}
        renderTags={rootProps.renderTags ?? renderTags}
        renderOption={renderOption}
        id={ariaId}
        autoComplete
        disableCloseOnSelect={!!props.multiple || !!props.disableCloseOnSelect}
        isOptionEqualToValue={isOptionEqualToValue}
        clearText={clearText}
        renderInput={(params) => (
          <TextFieldBase
            {...TextFieldProps}
            {...params.InputProps}
            name={name}
            inputRef={TextFieldProps?.inputRef || params.InputProps.ref}
            placeholder={isEmpty(fieldValue) ? placeholder : ''}
            inputProps={{
              ...params.inputProps,
              ...fieldAriaProps,
              ...TextFieldProps?.inputProps,
              className: classnames(
                TextFieldProps?.inputProps?.className,
                params.inputProps.className,
                slotClasses.select,
              ),
            }}
            className={classnames(
              TextFieldProps?.className,
              slotClasses.field,
              params.InputProps.className,
              'TextField-field',
            )}
            startAdornment={
              <>
                {asyncMode === 'search' ? (
                  <>
                    <FeatherIcon className={slotClasses.searchIcon} name={IconName.Search} />
                    {params.InputProps.startAdornment}
                  </>
                ) : (
                  params.InputProps.startAdornment
                )}
              </>
            }
            endAdornment={
              !props.disableClearable && asyncMode === 'search'
                ? (asyncFieldProps?.inputValue || fieldValue) && (
                    <AutocompleteEndAdornment className={slotClasses.endAdornment}>
                      <AutocompleteClearSearchButton
                        variant="text"
                        color="secondary"
                        onClick={handleClearInput}
                        className={slotClasses.clearSearchButton}
                        disabled={rootProps.loading || asyncFieldProps?.loading}
                      >
                        {clearText}
                      </AutocompleteClearSearchButton>
                    </AutocompleteEndAdornment>
                  )
                : params.InputProps.endAdornment
            }
          />
        )}
        {...asyncFieldProps}
        loading={rootProps.loading || asyncFieldProps?.loading}
        disabled={disabled}
        getOptionLabel={getOptionLabel}
        options={options || []}
        onChange={handleChange}
        onBlur={handleBlur}
        className={classnames(slotClasses.field, className)}
        inputValue={isAsync ? asyncFieldProps?.inputValue : inputValue}
        onInputChange={
          asyncFieldProps?.onInputChange !== undefined ? asyncFieldProps?.onInputChange : handleInputChange
        }
        filterOptions={asyncMode === 'search' ? SEARCH_MODE_FILTER_OPTIONS : props.filterOptions}
      />
    </FieldWrapper>
  )
}

const AutocompleteField = React.memo(_AutocompleteField) as typeof _AutocompleteField

export default AutocompleteField
